import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { byReportType } from 'relevant-shared/reportData/reportType';
import { demoMessage, isDemoUser } from '../../DemoImport/utils';
import { Reports } from '../../../api/relevant';
import useReports from '../../../hooks/useBreakpoint/useReports';
import { ConfirmDialog } from '../../ConfirmDialog';
import Base from '../../../layouts/Base';
import { stores } from '../../../stores';

function useSaveReport({
	isPrivate,
	report,
	reportComponentRef,
	type,
	subscriptions,
	setStatus,
}) {
	const history = useHistory();
	const { updateReports } = useReports();
	return useCallback(async () => {
		setStatus('saving');
		if (isDemoUser()) {
			demoMessage();
		} else if (reportComponentRef?.current) {
			// Extra confirmation if report is shared or has subscriptions
			if (report && !report.isNew) {
				const isPublic = report?.isPublic;
				const subs = subscriptions.length;
				const hasSubscriptions = subs > 0;
				if (isPublic || hasSubscriptions) {
					const ok = await Base.renderGlobal((closeFn) => {
						return (
							<ConfirmDialog
								open
								text={(
									<>
										<span className="whitespace-pre-wrap">
											This report
											{isPublic && <> is <span className="font-bold">publicly shared</span></>}
											{isPublic && hasSubscriptions && ' and'}
											{hasSubscriptions && <> has <span className="font-bold">{subs} subscriber{subs > 1 && 's'}</span></>}
											. Are you sure you want to change this report?
										</span>
									</>
								)}
								confirmLabel="Save"
								dontShowAgain="save-report-shared"
								onAny={closeFn}
								className="max-w-[500px]"
							/>
						);
					});
					if (!ok) {
						return false;
					}
				}
			}
			const settings = reportComponentRef.current.getCurrentProperties();
			// Convert to backend's legacy format
			const formattedSubscriptions = subscriptions.map(({ addedByUserId, occurrence, recipient }) => ({
				addedByUserId,
				occurences: [{ type: occurrence }],
				...('id' in recipient ? { recipientUserIds: [recipient.id] } : { emailOnlyRecipients: [recipient.email] }),
			}));
			try {
				const params = {
					settings,
					subscriptions: formattedSubscriptions,
					userId: isPrivate ? stores.identity.user().id : null,
				};
				if (!report?.isNew) {
					await report.updateReport(params);
				} else {
					const { id } = await Reports.call('addReport', {
						...params,
						tag: byReportType[type].tag,
					});
					// Navigate to the newly created report, without keeping 'add' in history
					const path = history.location.pathname.replace('/add', '');
					history.replace(`${path}/${id}`);
				}
				updateReports();
				setStatus('saved');
				return true;
			} catch (error) {
				setStatus('saveError');
				console.error('Failed to save report', error);
				return false;
			}
		}
	}, [history, isPrivate, report, reportComponentRef, setStatus, subscriptions, type, updateReports]);
}

export default useSaveReport;
